import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import styles from "./ResetPasswordForm.module.css";
import * as Yup from "yup";
import { requestOTP, updatePassword, verifyOTP } from "../../api/Api";
import { showToast } from "../../utils/toastUtils";
import { useNavigate } from "react-router-dom";
import CustomButton from "../CustomButton";

const ResetPasswordForm = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");

  return (
    <div className="w-100">
      <div className="text-center">
        <h1>Reset Password</h1>
      </div>

      {step === 1 ? (
        <ValidateAccount setStep={setStep} setEmail={setEmail} />
      ) : step === 2 ? (
        <OPTValidate setStep={setStep} email={email} />
      ) : (
        <NewPassword setStep={setStep} email={email} />
      )}
    </div>
  );
};

export default ResetPasswordForm;

const ValidateAccount = ({ setStep, setEmail }) => {
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Email is required"),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      console.log(values);
      setLoading(true);
      requestOTP({ email: values.email })
        .then((res) => {
          setLoading(false);
          if (res.status === 200) {
            showToast(res.message, "success");
            setStep(2);
            setEmail(values.email);
          } else if (res.status === 404) {
            showToast(
              "User does not exist. Please check your email address.",
              "error"
            );
          } else {
            showToast(
              res.message || "An error occurred. Please try again.",
              "error"
            );
          }
        })
        .catch((error) => {
          setLoading(false);
          if (error.response && error.response.status === 404) {
            showToast(
              "User does not exist. Please check your email address.",
              "error"
            );
          } else {
            showToast("An error occurred. Please try again.", "error");
          }
        });
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="d-flex align-items-center column-gap-2 mt-5">
        <span
          className="bg-secondary-subtle text-secondary fw-bold d-block rounded-circle text-center"
          style={{ width: "25px", height: "25px" }}
        >
          1
        </span>
        <span className="text-secondary fw-bold" style={{ fontSize: "14px" }}>
          Verify Your Account
        </span>
      </div>

      <p className="small mt-4">
        Forgot your password? Don't worry! Enter your registered email address
        below, and we'll send you a One-Time Password (OTP) to verify your
        account
      </p>

      <div className="mb-3">
        <label htmlFor="email" className="form-label text-color-tertiary">
          <p className="small mb-0">E-mail</p>
        </label>
        <input
          type="email"
          placeholder="Enter your email"
          name="email"
          className={`form-control ${
            formik.touched.email && formik.errors.email ? "is-invalid" : ""
          }`}
          {...formik.getFieldProps("email")}
        />
        {formik.touched.email && formik.errors.email && (
          <div className="invalid-feedback">{formik.errors.email}</div>
        )}
      </div>

      <CustomButton
        type="submit"
        className={`btn bg-custom-secondary ${styles.submitButton} w-100 mt-3 py-3`}
        disabled={loading || formik.isSubmitting}
      >
        <h6 className={`mb-0 ${loading ? "text-black" : "text-white"}`}>
          {loading ? "Sending..." : "Send OTP"}
        </h6>
      </CustomButton>
    </form>
  );
};

const OPTValidate = ({ setStep, email }) => {
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(60);
  const [showTimer, setShowTimer] = useState(true);

  const validationSchema = Yup.object({
    otp: Yup.array()
      .of(Yup.string().matches(/^\d?$/, "Only digits are allowed"))
      .test(
        "complete",
        "OTP must be complete",
        (otp) => otp && otp.filter(Boolean).length === otp.length
      ),
  });

  const formik = useFormik({
    initialValues: {
      otp: ["", "", "", "", "", ""],
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      const otp = values.otp.join("");
      console.log("OTP Entered:", otp);
      setLoading(true);
      let obj = {
        email: email,
        otp: otp,
      };

      verifyOTP(obj)
        .then((res) => {
          setLoading(false);
          if (res.status === 200) {
            showToast(res.message, "success");
            setStep(3);
          } else if (res.status === 400) {
            showToast("Invalid or expired OTP. Please try again.", "error");
          } else if (res.status === 404) {
            showToast("OTP not found. Please request a new OTP.", "error");
          } else {
            showToast(
              res.message || "OTP verification failed. Please try again.",
              "error"
            );
          }
        })
        .catch((error) => {
          setLoading(false);
          if (error.response) {
            if (error.response.status === 400) {
              showToast("Invalid or expired OTP. Please try again.", "error");
            } else if (error.response.status === 404) {
              showToast("OTP not found. Please request a new OTP.", "error");
            } else {
              showToast(
                error.response.data?.message ||
                  "OTP verification failed. Please try again.",
                "error"
              );
            }
          } else {
            showToast(
              "Network error. Please check your connection and try again.",
              "error"
            );
          }
        });
    },
  });

  const handleInputChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, ""); // only digits
    if (value) {
      const newOtp = [...formik.values.otp];
      newOtp[index] = value[0];
      formik.setFieldValue("otp", newOtp);
      if (index < 5) {
        document.getElementById(`otp-input-${index + 1}`).focus();
      }
    } else {
      const newOtp = [...formik.values.otp];
      newOtp[index] = "";
      formik.setFieldValue("otp", newOtp);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (formik.values.otp[index] === "") {
        if (index > 0) {
          document.getElementById(`otp-input-${index - 1}`).focus();
        }
      }
    }
  };

  const handlePaste = (e) => {
    const pastedData = e.clipboardData
      .getData("Text")
      .trim()
      .replace(/\D/g, "");
    if (pastedData.length === 6) {
      const newOtp = pastedData.split("").slice(0, 6);
      formik.setFieldValue("otp", newOtp);
      setTimeout(() => {
        document.getElementById("otp-input-5").focus();
      }, 0);
    }
  };

  useEffect(() => {
    let interval;
    if (showTimer && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }

    if (timer === 0) {
      setShowTimer(false);
    }

    return () => clearInterval(interval);
  }, [timer, showTimer]);

  const handleRequestOtp = () => {
    setTimer(60);
    setShowTimer(true);
  };

  const resentOTP = () => {
    if (!showTimer) {
      requestOTP({ email: email })
        .then((res) => {
          if (res.status === 200) {
            showToast(res.message, "success");
            handleRequestOtp();
          } else {
            showToast(
              res.message || "Failed to resend OTP. Please try again.",
              "error"
            );
          }
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            showToast(
              "User does not exist. Please check your email address.",
              "error"
            );
          } else {
            showToast("Failed to resend OTP. Please try again.", "error");
          }
        });
    } else {
      showToast(
        "Please wait until the timer ends before requesting a new OTP",
        "warning"
      );
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="d-flex align-items-center column-gap-2 mt-5">
        <span
          className="bg-secondary-subtle text-secondary fw-bold d-block rounded-circle text-center"
          style={{ width: "25px", height: "25px" }}
        >
          2
        </span>
        <span className="text-secondary fw-bold" style={{ fontSize: "14px" }}>
          Verify OTP
        </span>
      </div>

      <p className="small mt-4">
        We've sent a One-Time Password (OTP) to your registered email. Please
        enter the OTP below to verify your account and proceed.
      </p>
      <p className="small mt-2">
        This OTP is valid for 10 minutes. Please complete the verification
        within this time frame.
      </p>

      <label htmlFor="email" className="form-label text-color-tertiary">
        <p className="small mb-0">OTP</p>
      </label>
      <div className="d-flex column-gap-xl-3 column-gap-md-1 column-gap-3 align-items-center">
        {formik.values.otp.map((_, index) => (
          <input
            key={index}
            id={`otp-input-${index}`}
            name={`otp[${index}]`}
            type="text"
            maxLength="1"
            className={`${styles.otpInput}`}
            value={formik.values.otp[index]}
            onChange={(e) => handleInputChange(e, index)}
            onBlur={() => formik.setFieldTouched(`otp[${index}]`, true)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
          />
        ))}
      </div>
      {formik.errors.otp &&
        formik.touched.otp &&
        typeof formik.errors.otp === "string" && (
          <div className="text-danger small mt-2">{formik.errors.otp}</div>
        )}
      <div className="d-flex justify-content-between mt-2">
        <div>
          <p className="small mb-0">
            Didn't receive the OTP?{" "}
            <span
              onClick={resentOTP}
              className="text-color-cyan text-decoration-underline"
              style={{ cursor: "pointer" }}
            >
              Resend OTP
            </span>
          </p>
          <p className="small">
            Make sure to check your spam or junk folder too!
          </p>
        </div>
        {showTimer ? (
          <span className="small text-secondary">{timer}</span>
        ) : null}
      </div>

      <button
        type="submit"
        className={`btn bg-custom-secondary ${styles.submitButton} w-100 mt-3 py-3`}
        disabled={loading || formik.isSubmitting}
      >
        <h6 className={`mb-0 ${loading ? "text-black" : "text-white"}`}>
          {loading ? "Submitting..." : "Submit OTP"}
        </h6>
      </button>
    </form>
  );
};
const NewPassword = ({ setStep, email }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
      .required("Confirm Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      let obj = {
        email: email,
        password: values.newPassword,
      };
      updatePassword(obj).then((res) => {
        setLoading(false);
        if (res.status === 200) {
          showToast(res.message, "success");
          navigate("/");
          setStep(3);
        }
      });
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="d-flex align-items-center column-gap-2 mt-5">
        <span
          className="bg-secondary-subtle text-secondary fw-bold d-block rounded-circle text-center"
          style={{ width: "25px", height: "25px" }}
        >
          3
        </span>
        <span className="text-secondary fw-bold" style={{ fontSize: "14px" }}>
          Set new password
        </span>
      </div>

      <p className="small mt-4">
        Create a new password for your account. Make sure it’s strong and easy
        for you to remember.
      </p>

      <div className="mb-3">
        <label htmlFor="password" className="form-label text-color-tertiary">
          <p className="small mb-0">New Password</p>
        </label>
        <input
          type="password"
          placeholder="Enter your password"
          name="password"
          className={`form-control ${
            formik.touched.newPassword && formik.errors.newPassword
              ? "is-invalid"
              : ""
          }`}
          {...formik.getFieldProps("newPassword")}
        />
        {formik.touched.newPassword && formik.errors.newPassword && (
          <div className="invalid-feedback">{formik.errors.newPassword}</div>
        )}
      </div>
      <div className="mb-3">
        <label htmlFor="password" className="form-label text-color-tertiary">
          <p className="small mb-0">Confirm Password</p>
        </label>
        <input
          type="password"
          placeholder="Enter your password"
          name="confirmPassword"
          className={`form-control ${
            formik.touched.confirmPassword && formik.errors.confirmPassword
              ? "is-invalid"
              : ""
          }`}
          {...formik.getFieldProps("confirmPassword")}
        />
        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
          <div className="invalid-feedback">
            {formik.errors.confirmPassword}
          </div>
        )}
      </div>

      <button
        type="submit"
        className={`btn bg-custom-secondary ${styles.submitButton} w-100 mt-3 py-3`}
        disabled={loading || formik.isSubmitting}
      >
        <h6 className="mb-0">{loading ? "Changing..." : "Change Password"}</h6>
      </button>
    </form>
  );
};
